import type {
  ForgeAppList,
  ForgeHostColumn,
  ForgeProvider,
  PersonLinksView,
  PersonLinkView,
} from '@/lib/api/generated/model';

import {
  daysLeft,
  linkCell,
  linkStanding,
  type PeopleFilter,
} from './linkedAccounts';

/**
 * Admin → Commit identity, the answer and the queue (spec 164). Pure: how
 * many people push as themselves, and who needs a look first.
 */

/**
 * Where a person stands over every host in use, each person in exactly one:
 * a broken link first, then nothing working, then some hosts, then all.
 * An expiring token still works, so it still pushes as them.
 */
export const COVERAGE_BUCKETS = [
  'themselves',
  'partly',
  'relink',
  'not-linked',
] as const;

export type CoverageBucket = (typeof COVERAGE_BUCKETS)[number];

/** A working link: one runs can use now, expiring or not. */
function works(link: PersonLinkView | null): boolean {
  return link?.status === 'working';
}

export function coverageBucket(
  row: PersonLinksView,
  inUse: readonly string[],
  now: Date,
): CoverageBucket {
  const links = inUse.map((host) => linkCell(row, host));
  if (links.some((link) => link && linkStanding(link, now) === 'broken')) {
    return 'relink';
  }
  const working = links.filter(works).length;
  if (working === inUse.length) return 'themselves';
  return working === 0 ? 'not-linked' : 'partly';
}

export type Coverage = Record<CoverageBucket, number> & { total: number };

export function coverage(
  rows: readonly PersonLinksView[],
  inUse: readonly string[],
  now: Date,
): Coverage {
  const counts: Coverage = {
    themselves: 0,
    partly: 0,
    relink: 0,
    'not-linked': 0,
    total: rows.length,
  };
  for (const row of rows) counts[coverageBucket(row, inUse, now)] += 1;
  return counts;
}

/** Each bucket's colour on the ring, and on its legend dot. */
export const BUCKET_STROKE: Record<CoverageBucket, string> = {
  themselves: 'stroke-state-success',
  partly: 'stroke-state-info',
  relink: 'stroke-state-danger',
  'not-linked': 'stroke-muted-foreground/50',
};

export const BUCKET_DOT: Record<CoverageBucket, string> = {
  themselves: 'bg-state-success',
  partly: 'bg-state-info',
  relink: 'bg-state-danger',
  'not-linked': 'bg-muted-foreground/50',
};

export interface RingArc {
  bucket: CoverageBucket;
  /** Where it starts along the circle, from the top. */
  start: number;
  length: number;
}

/**
 * The ring's segments, in bucket order, with a hairline gap between them so
 * two never read as one. An empty bucket draws nothing.
 */
export function ringArcs(counts: Coverage, circumference: number): RingArc[] {
  if (counts.total === 0) return [];
  const gap = counts.total > 1 ? 2 : 0;
  const arcs: RingArc[] = [];
  let start = 0;
  for (const bucket of COVERAGE_BUCKETS) {
    const length = (counts[bucket] / counts.total) * circumference;
    if (length > 0) {
      arcs.push({ bucket, start, length: Math.max(length - gap, 1) });
    }
    start += length;
  }
  return arcs;
}

/**
 * The people list's slice a legend entry opens, if any: everyone who pushes
 * as themselves has nothing to look at.
 */
export const BUCKET_FILTER: Record<CoverageBucket, PeopleFilter | null> = {
  themselves: null,
  partly: 'missing',
  relink: 'broken',
  'not-linked': 'missing',
};

/** How often one person may be reminded about one host (service spec 143). */
export const REMIND_EVERY_MS = 86_400_000;

/** When the person was last reminded about the host, if ever. */
export function remindedAt(row: PersonLinksView, host: string): string | null {
  return (
    row.reminders?.find((entry) => entry.host === host)?.reminded_at ?? null
  );
}

/** Whether a reminder would be refused: one went within the last day. */
export function remindedRecently(at: string | null, now: Date): boolean {
  if (!at) return false;
  return now.getTime() - Date.parse(at) < REMIND_EVERY_MS;
}

/**
 * One thing to look at (spec 164 US2), worst first:
 * - **blocked:** a workspace requires the person's own account on a host and
 *   they have no working link there, so they cannot start runs in it;
 * - **broken:** a link that stopped working, which runs fall back from;
 * - **expiring:** a personal token about to end;
 * - **sign-in:** a host in use where people can only paste a token.
 *
 * A link an admin removed is not listed as broken: someone meant it. It is
 * listed where a workspace requires it, because then it stops runs.
 */
export type Look =
  | {
      kind: 'blocked' | 'broken' | 'expiring';
      key: string;
      userId: string;
      person: string;
      provider: ForgeProvider;
      host: string;
      link: PersonLinkView | null;
      /** Where runs are stopped (blocked only). */
      workspaceIds: readonly string[];
      /** Days left on the token (expiring only). */
      days: number | null;
      remindedAt: string | null;
    }
  | { kind: 'sign-in'; key: string; provider: ForgeProvider; host: string }
  | {
      /** One forge account, linked by several people (spec 169). */
      kind: 'shared';
      key: string;
      provider: ForgeProvider;
      host: string;
      username: string;
      /** Who linked it, by name. */
      people: readonly string[];
    };

export type PersonLook = Exclude<Look, { kind: 'sign-in' | 'shared' }>;

const LOOK_ORDER: Record<Look['kind'], number> = {
  blocked: 0,
  // Above broken links: a shared account puts one person's name on another's
  // work, which no fallback undoes.
  shared: 1,
  broken: 2,
  expiring: 3,
  'sign-in': 4,
};

export function needsALook(
  rows: readonly PersonLinksView[],
  columns: readonly ForgeHostColumn[],
  apps: ForgeAppList | null,
  now: Date,
): Look[] {
  const inUse = columns.filter((column) => !column.unused);
  const looks: Look[] = [];
  for (const row of rows) {
    for (const column of inUse) {
      const link = linkCell(row, column.host);
      const base = {
        key: `${row.person.id}:${column.host}`,
        userId: row.person.id,
        person: row.person.display_name,
        provider: column.provider,
        host: column.host,
        link,
        workspaceIds: [] as readonly string[],
        days: null,
        remindedAt: remindedAt(row, column.host),
      };
      const required = column.required_workspace_ids ?? [];
      if (!works(link) && required.length > 0) {
        looks.push({ ...base, kind: 'blocked', workspaceIds: required });
      } else if (
        link &&
        linkStanding(link, now) === 'broken' &&
        link.status_reason !== 'admin'
      ) {
        looks.push({ ...base, kind: 'broken' });
      } else if (link && linkStanding(link, now) === 'expiring') {
        looks.push({ ...base, kind: 'expiring', days: daysLeft(link, now) });
      }
    }
  }
  looks.push(...sharedAccounts(rows, inUse));
  if (apps?.available) {
    const configured = new Set(
      apps.items.filter((app) => app.configured).map((app) => app.host),
    );
    for (const column of inUse) {
      if (!configured.has(column.host)) {
        looks.push({
          kind: 'sign-in',
          key: `sign-in:${column.host}`,
          provider: column.provider,
          host: column.host,
        });
      }
    }
  }
  return looks.sort(
    (a, b) =>
      LOOK_ORDER[a.kind] - LOOK_ORDER[b.kind] ||
      lookName(a).localeCompare(lookName(b)) ||
      a.host.localeCompare(b.host),
  );
}

function lookName(look: Look): string {
  if (look.kind === 'sign-in') return look.host;
  if (look.kind === 'shared') return look.username;
  return look.person;
}

/**
 * Forge accounts linked by more than one person on a host in use (spec 169).
 * By username, ignoring case, over links that are not removed: two people on
 * one account means commits pushed for one of them carry the other's name.
 * A warning, not a block: some teams link a bot account on purpose.
 */
export function sharedAccounts(
  rows: readonly PersonLinksView[],
  columns: readonly ForgeHostColumn[],
): Extract<Look, { kind: 'shared' }>[] {
  const found: Extract<Look, { kind: 'shared' }>[] = [];
  for (const column of columns) {
    const byUser = new Map<string, { username: string; people: string[] }>();
    for (const row of rows) {
      const link = linkCell(row, column.host);
      if (!link || link.status === 'revoked') continue;
      const key = link.forge_username.toLowerCase();
      const entry = byUser.get(key) ?? {
        username: link.forge_username,
        people: [],
      };
      entry.people.push(row.person.display_name);
      byUser.set(key, entry);
    }
    for (const { username, people } of byUser.values()) {
      if (people.length < 2) continue;
      found.push({
        kind: 'shared',
        key: `shared:${column.host}:${username.toLowerCase()}`,
        provider: column.provider,
        host: column.host,
        username,
        people: [...people].sort((a, b) => a.localeCompare(b)),
      });
    }
  }
  return found;
}

/** Workspace names for a list of ids, in order; unknown ids are dropped. */
export function namesOf(
  ids: readonly string[],
  names: ReadonlyMap<string, string>,
): string[] {
  return ids.flatMap((id) => {
    const name = names.get(id);
    return name ? [name] : [];
  });
}

export interface WorkspaceImpact {
  /** The GitLab and GitHub hosts the workspace connects to. */
  hosts: readonly string[];
  /** Who has no working link on at least one of them, by name. */
  stopped: readonly PersonLinksView[];
}

/**
 * Who a workspace that requires personal accounts stops, or would stop (spec
 * 165): everyone without a working link on one of its hosts. A workspace that
 * connects to no forge stops nobody.
 */
export function workspaceImpact(
  workspaceId: string,
  rows: readonly PersonLinksView[],
  columns: readonly ForgeHostColumn[],
): WorkspaceImpact {
  const hosts = columns
    .filter((column) => (column.workspace_ids ?? []).includes(workspaceId))
    .map((column) => column.host);
  const stopped = rows
    .filter((row) => hosts.some((host) => !works(linkCell(row, host))))
    .sort((a, b) => a.person.display_name.localeCompare(b.person.display_name));
  return { hosts, stopped };
}

/** A few names and how many more: "Alice, Bob, Carol and 2 more". */
export function joinNames(names: readonly string[], shown = 3): string {
  const first = names.slice(0, shown);
  const more = names.length - first.length;
  if (more > 0) return `${first.join(', ')} and ${String(more)} more`;
  if (first.length <= 1) return first.join('');
  return `${first.slice(0, -1).join(', ')} and ${first[first.length - 1] ?? ''}`;
}

/** The same, for people from the list. */
export function someNames(rows: readonly PersonLinksView[], shown = 3): string {
  return joinNames(
    rows.map((row) => row.person.display_name),
    shown,
  );
}
